/**
 * Plan, add-on and coupon pricing for member checkout (AR-420).
 *
 * The numbers come from the server: plans and the Accelerator price from
 * `/api/checkout/catalogue` (seeded from the Stripe catalogue, AR-419), a
 * coupon from Stripe through `/api/checkout/coupon`. This module only lays them
 * out as order lines. Once the subscription exists, the amount Stripe will
 * charge (`amountDueCents`) is shown instead of this estimate.
 */

export interface CheckoutPlan {
  /** The `?plan=` key: individual, growth, team, enterprise. */
  id: string;
  name: string;
  /** Recurring subscription price, in cents. */
  monthlyCents: number;
  /** One-time account setup / activation fee, in cents. */
  setupFeeCents: number;
  documentsNote: string;
}

export interface AcceleratorCohort {
  id: string;
  label: string;
}

export interface CouponDefinition {
  code: string;
  label: string;
  kind: 'amount' | 'percent';
  /** Cents when `kind` is `amount`, whole percent when `kind` is `percent`. */
  value: number;
}

export interface OrderLine {
  id: string;
  label: string;
  cents: number;
  /** True for the subscription line — charged now and again every month. */
  recurring?: boolean;
}

export interface OrderSummary {
  lines: OrderLine[];
  monthlyCents: number;
  dueNowCents: number;
}

const DOCUMENTS_NOTE =
  'You will be asked to provide these required company documents during your account setup: EIN Letter and LLC or SCORP';

export const DEFAULT_PLAN_ID = 'team';

/** A catalogue row as the order summary uses it. */
export const toCheckoutPlan = (row: { key: string; name: string; monthlyCents: number; setupFeeCents: number }): CheckoutPlan => ({
  id: row.key,
  name: /plan$/i.test(row.name) ? row.name : `${row.name} Plan`,
  monthlyCents: row.monthlyCents,
  setupFeeCents: row.setupFeeCents,
  documentsNote: DOCUMENTS_NOTE,
});

export const ACCELERATOR_COHORTS: AcceleratorCohort[] = [
  { id: 'sep-2026', label: 'September 8–12, 2026' },
  { id: 'oct-2026', label: 'October 13–17, 2026' },
  { id: 'nov-2026', label: 'November 10–14, 2026' },
];

export const ACCELERATOR_FORMATS = [
  { id: 'in_person', label: 'In person — Atlanta, GA' },
  { id: 'virtual', label: 'Virtual — live online' },
] as const;

/** A server coupon result as an order-summary discount. */
export const toCouponDefinition = (c: {
  code: string;
  label: string;
  amountOffCents: number | null;
  percentOff: number | null;
}): CouponDefinition =>
  c.percentOff
    ? { code: c.code, label: c.label === c.code ? `${c.percentOff}% off` : c.label, kind: 'percent', value: c.percentOff }
    : {
        code: c.code,
        label: c.label === c.code ? `$${((c.amountOffCents ?? 0) / 100).toFixed(2)} off your first payment` : c.label,
        kind: 'amount',
        value: c.amountOffCents ?? 0,
      };

export const formatCents = (cents: number): string =>
  `${cents < 0 ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)} USD`;

interface OrderInput {
  plan: CheckoutPlan;
  acceleratorAdded: boolean;
  /** Accelerator price from the catalogue. */
  acceleratorCents: number;
  coupon?: CouponDefinition | null;
}

/**
 * Builds the itemised order. Adding the Accelerator waives the one-time setup
 * fee (the server leaves the fee off the first invoice), shown as an explicit credit line so the member can
 * see why the total changed.
 */
export const buildOrderSummary = ({
  plan,
  acceleratorAdded,
  acceleratorCents,
  coupon,
}: OrderInput): OrderSummary => {
  const lines: OrderLine[] = [
    { id: 'plan', label: 'Subtotal (monthly)', cents: plan.monthlyCents, recurring: true },
    { id: 'setup', label: 'One-Time Account Setup Fee', cents: plan.setupFeeCents },
  ];

  if (acceleratorAdded) {
    lines.push({ id: 'accelerator', label: 'Accelerator Course', cents: acceleratorCents });
    lines.push({
      id: 'accelerator-waiver',
      label: 'Account setup fee waived (Accelerator)',
      cents: -plan.setupFeeCents,
    });
  }

  const preCouponDueNow = lines.reduce((total, line) => total + line.cents, 0);

  if (coupon) {
    const discount =
      coupon.kind === 'amount'
        ? Math.min(coupon.value, preCouponDueNow)
        : Math.round((preCouponDueNow * coupon.value) / 100);
    lines.push({ id: 'coupon', label: `Coupon ${coupon.code}`, cents: -discount });
  }

  const dueNowCents = lines.reduce((total, line) => total + line.cents, 0);

  return { lines, monthlyCents: plan.monthlyCents, dueNowCents: Math.max(0, dueNowCents) };
};
